import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import PMListboxFormik from "../../../utils/PMListboxFormik";
import { PMDialog } from "../../../utils/PMDialog";
import { QuillEditor } from "../../../utils/quill-editor";
import PMSwitch from "../../../utils/PMSwitch";
import { fetchContact } from "../../../../api/customer/contact";
import { promangToast } from "../../../../operationFeedback";
import { FormikSendRecipientPicker, SendDialogActions } from "../../shared/SendRecipientPicker";
import { isRecipientList } from "../../shared/recipientOptions";
import { runSendReportOperation } from "./runSendReportOperation";

export const ReportSendDialog = ({ onClose, open, isLoading, report, onRefresh }) => {
  const { t } = useTranslation();
  const currentCompany = useSelector((state) => state.session.currentCompany);
  const currentUser = useSelector((state) => state.session.currentUser);
  const [contact, setContact] = useState(null);
  const [isLoadingContact, setIsLoadingContact] = useState(false);

  useEffect(() => {
    let active = true;
    if (!open || !report?.contact?.id) {
      setContact(null);
      return () => { active = false; };
    }
    setContact(null);
    setIsLoadingContact(true);
    fetchContact(report.contact.id)
      .then((response) => { if (active) setContact(response?.data || null); })
      .catch(() => {
        if (active) promangToast.error(t("Failed to load contact information"));
      })
      .finally(() => { if (active) setIsLoadingContact(false); });
    return () => { active = false; };
  }, [open, report?.contact?.id, t]);

  const recipientOptions = [
    currentUser?.email && { key: "me", label: t("Me"), email: currentUser.email, name: currentUser.name },
    report?.customer?.email && { key: "customer", label: t("Customer"), email: report.customer.email, name: report.customer.name, reportType: "customer_report" },
    { key: "contact", label: isLoadingContact ? t("Loading...") : t("Contact"), email: contact?.email, name: contact?.name || contact?.displayTitle, disabled: isLoadingContact || !contact?.email },
  ].filter(Boolean);

  return (
    <PMDialog open={open} onClose={onClose} title={t("Send Report")} size="2xl" closeOnOutsideClick>
      <p className="mb-5 text-sm text-amber-700">{t("Please note that this action cannot be undone")}</p>
      {isLoading ? <ResourceLoading /> : !report?.id ? (
        <p className="text-sm text-red-600">{t("Missing ID")}</p>
      ) : (
        <Formik
          enableReinitialize
          initialValues={{
            id: report.id,
            sendReport: true,
            printReportType: "",
            email: "",
            name: "",
            allow_review: false,
            message: "",
          }}
          validationSchema={Yup.object({
            email: Yup.string().trim().test("recipients", t("documentationSend.invalid_email"), isRecipientList).required(t("documentationSend.email_required")),
            printReportType: Yup.string().required(t("Report Type")),
          })}
          onSubmit={async (values, helpers) => {
            try {
              onClose?.();
              await runSendReportOperation({
                values,
                report,
                companyId: currentCompany?.id,
                onRefresh,
                t,
              });
              helpers.setStatus({ success: true });
            } catch (error) {
              helpers.setStatus({ success: false });
              helpers.setErrors({ submit: error?.message || t("operation_feedback.generic_error") });
            } finally {
              helpers.setSubmitting(false);
            }
          }}
        >
          {(formik) => (
            <Form className="space-y-5">
              <FormikSendRecipientPicker
                formik={formik}
                multiple
                autoFocus
                customerId={report?.customer?.id}
                options={recipientOptions}
                onSelect={(option) => {
                  if (option.reportType) formik.setFieldValue("printReportType", option.reportType);
                }}
              />
              <PMListboxFormik
                label={t("Report Type")}
                name="printReportType"
                options={[
                  { name: "printReportType", value: "report", label: t("Report") },
                  { name: "printReportType", value: "customer_report", label: t("Customer Report") },
                  { name: "printReportType", value: "note", label: t("Note") },
                ]}
                {...formik}
              />
              <PMSwitch name="allow_review" label="Tillat Anmeldelse" {...formik} />
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">{t("Message to customer")}</label>
                <QuillEditor name="message" rows={4} {...formik} />
              </div>
              {formik.errors.submit && <p role="alert" className="text-sm text-red-600">{formik.errors.submit}</p>}
              <SendDialogActions
                onCancel={onClose}
                onSubmit={formik.submitForm}
                submitting={formik.isSubmitting}
                cancelLabel={t("Close")}
                sendLabel={t("Send")}
                sendingLabel={t("Sending...")}
              />
            </Form>
          )}
        </Formik>
      )}
    </PMDialog>
  );
};
